import i18n from '@/i18n'
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAgency } from '../contexts/AgencyContext';

export interface AgencyUser {
  id: string;
  auth_user_id: string;
  agency_id: string;
  created_at: string;
  updated_at: string;
  email?: string;
  name?: string;
}

export function useAgencyUsers() {
  const { agency } = useAgency();
  const [users, setUsers] = useState<AgencyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      if (!agency?.id) {
        console.log('⏳ [useAgencyUsers] Waiting for agency_id...');
        setUsers([]);
        setLoading(false);
        return;
      }

      console.log('🔍 [useAgencyUsers] Fetching agency users for agency_id:', agency.id);

      const { data: agencyUsers, error: agencyError } = await supabase
        .from('agency_users')
        .select('*')
        .eq('agency_id', agency.id)
        .order('created_at', { ascending: false });

      if (agencyError) throw agencyError;

      console.log('✅ [useAgencyUsers] Users loaded:', agencyUsers?.length || 0);

      setUsers((agencyUsers || []) as AgencyUser[]);
    } catch (err) {
      console.error('❌ [useAgencyUsers] Error fetching agency users:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const addUser = async (name: string, email: string) => {
    try {
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      if (!agency?.id) {
        console.error('❌ [addUser] Agency not available:', agency);
        throw new Error(i18n.t('agency:errors.agencyIdUnavailableReload'));
      }

      console.log('📝 [addUser] Adding user:', { name, email, agency_id: agency.id });
      console.log('📝 [addUser] Full agency object:', agency);

      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-agency-user`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          name,
          email,
          agency_id: agency.id
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao adicionar usuário');
      }

      console.log('✅ [addUser] User added successfully:', result);

      await fetchUsers();

      const isNewUser = result.data?.is_new_user || false;
      const emailSent = result.data?.email_sent || false;

      let successMessage = result.message;
      if (isNewUser && emailSent) {
        successMessage = `Usuário criado com sucesso! Um e-mail de boas-vindas com a senha de acesso foi enviado para ${email}.`;
      }

      return {
        success: true,
        message: successMessage,
        isNewUser: isNewUser,
        emailSent: emailSent
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao adicionar usuário';
      setError(message);
      throw new Error(message);
    }
  };

  const removeUser = async (userId: string) => {
    try {
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      if (!agency?.id) {
        throw new Error(i18n.t('agency:errors.agencyIdUnavailable'));
      }

      console.log('🗑️ [removeUser] Removing user:', userId);

      const { data: userToRemove } = await supabase
        .from('agency_users')
        .select('agency_id')
        .eq('id', userId)
        .eq('agency_id', agency.id)
        .maybeSingle();

      if (!userToRemove) {
        throw new Error(i18n.t('agency:errors.userNotInAgency'));
      }

      const { error: deleteError } = await supabase
        .from('agency_users')
        .delete()
        .eq('id', userId)
        .eq('agency_id', agency.id);

      if (deleteError) throw deleteError;

      console.log('✅ [removeUser] User removed successfully');

      await fetchUsers();
      return { success: true, message: 'Usuário removido com sucesso' };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao remover usuário';
      setError(message);
      throw new Error(message);
    }
  };

  useEffect(() => {
    if (agency?.id) {
      fetchUsers();
    }
  }, [agency?.id]);

  return {
    users,
    loading,
    error,
    fetchUsers,
    addUser,
    removeUser
  };
}
